import type { MouseEvent, ReactNode } from 'react';
import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import type { Element } from '../../format/form-definition';
import { elementName } from '../../texts/words';
import { useBuilder } from '../context';
import { elementType } from '../fieldsPalette/tiles';
import { CanvasField, UNTITLED } from './CanvasField';
import { StaticTextBlock } from '../../rendering/FormElements';
import { CanvasSection } from './CanvasSection';
import { useElementDrag } from '../drag-and-drop';
import { Droppable } from 'framework/dnd';
import { ElementToolbar } from './ElementToolbar';
import { holdsFields } from '../../format/elements';
import { fieldWidthOf } from '../../field-types';
import { FIELD_WIDTH_COLUMNS } from '../../format/fieldWidth';

type FrameLook = 'plain' | 'section' | 'group';

interface CanvasElementProps {
  element: Element;
  children?: ReactNode;
}

export const CanvasElement = ({ element, children }: CanvasElementProps) => {
  const { selectedId, openElement } = useBuilder();
  const drag = useElementDrag(element);
  const selected = selectedId === element.id;
  const frame: FrameLook =
    element.kind === 'section' ? 'section' : element.kind === 'field' && holdsFields(element) ? 'group' : 'plain';
  const open = (event: MouseEvent) => {
    event.stopPropagation();
    openElement(element.id);
  };

  return (
    <Droppable params={drag.params}>
      {({ droppableRef }) => (
        <Grid
          ref={droppableRef}
          size={columnSpan(element)}
          data-element-id={element.id}
          role="group"
          aria-label={`${elementType(element).name}: ${elementName(element)}`}
          onClick={open}
          sx={[frameLook(frame, selected), { minWidth: 0 }, drag.line]}
        >
          {selected && <ElementToolbar element={element} />}
          <ElementContent element={element}>{children}</ElementContent>
        </Grid>
      )}
    </Droppable>
  );
};

const columnSpan = (element: Element): number =>
  element.kind === 'field' ? FIELD_WIDTH_COLUMNS[fieldWidthOf(element)] : 6;

const ElementContent = ({ element, children }: CanvasElementProps) => {
  if (element.kind === 'section') return <CanvasSection section={element}>{children}</CanvasSection>;
  if (element.kind === 'staticText') return <CanvasStaticText text={element.text} />;
  return <CanvasField field={element}>{children}</CanvasField>;
};

const CanvasStaticText = ({ text }: { text: string }) => {
  return (
    <Stack useFlexGap direction="row" spacing={1} justifyContent="space-between" sx={{ pointerEvents: 'none' }}>
      {text.trim() ? <StaticTextBlock text={text} /> : <Typography color="text.disabled">{UNTITLED.text}</Typography>}
    </Stack>
  );
};

const BOXES = {
  plain: { p: 1.5 },
  section: { p: 2.5, border: 1, borderColor: 'divider' },
  group: { p: 2.5, border: 1, borderStyle: 'dashed', borderColor: 'divider' },
} as const;

const frameLook = (frame: FrameLook, selected: boolean) => {
  return {
    position: 'relative',
    borderRadius: 1,
    scrollMarginBlock: '24px',
    cursor: 'pointer',
    ...BOXES[frame],
    outline: selected ? 2 : '1px dashed',
    outlineColor: selected ? 'primary.main' : 'transparent',
    outlineOffset: frame === 'plain' ? 0 : 2,
    '&:hover:not(:has([data-element-id]:hover))': selected ? {} : { outlineColor: 'border.medium' },
  } as const;
};
